import * as React from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import type { ListingListResponse, TemplateBrandProps } from '@proptr/shared';
import {
  BANNER_RATIO_LABEL,
  DEFAULT_BRAND,
  ListingBanner,
} from '@proptr/creative-templates';
import { bannerListingFrom } from '../lib/bannerListing.js';
import { Button } from './ui/button.js';
import { cn } from '../lib/utils.js';

// Both ratios of one listing, side by side at a size you can actually
// judge. The inline preview is a scrolling column of 46 cards at 420px —
// good for scanning the set, useless for checking whether the price
// clears the QR on the vertical.
//
// Deliberately read-only: the QR drag lives on the inline preview, where
// the card is anchored and measurable. A second drag surface would be a
// second set of hero/canvas conversions to keep in step.

type ListingRow = ListingListResponse['items'][number];

export function FeedFullPreview({
  open,
  onClose,
  listings,
  brand,
  qrPosition,
  qrPositionVertical,
  includeVertical,
}: {
  open: boolean;
  onClose: () => void;
  listings: ListingRow[];
  brand: TemplateBrandProps;
  qrPosition: { x: number; y: number } | null;
  qrPositionVertical: { x: number; y: number } | null;
  /** When false only the square is shown — the feed exports nothing else. */
  includeVertical: boolean;
}) {
  const [index, setIndex] = React.useState(0);
  const [shown, setShown] = React.useState(false);
  const [visible, setVisible] = React.useState(false);

  // Mount/unmount around the transition so the panel animates both ways,
  // matching AddProptreeButtonModal rather than inventing a second feel.
  React.useEffect(() => {
    if (open) {
      setShown(true);
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      const r1 = requestAnimationFrame(() =>
        requestAnimationFrame(() => setVisible(true)),
      );
      return () => {
        cancelAnimationFrame(r1);
        document.body.style.overflow = prev;
      };
    }
    setVisible(false);
    const t = setTimeout(() => setShown(false), 200);
    return () => clearTimeout(t);
  }, [open]);

  // Opening on a listing that has since been filtered out would index past
  // the end; clamp rather than blank the panel.
  const count = listings.length;
  const safeIndex = count === 0 ? 0 : Math.min(index, count - 1);
  const listing = listings[safeIndex];

  const step = React.useCallback(
    (delta: number) => {
      if (count === 0) return;
      // Wrap, so paging never dead-ends on the first or last card.
      setIndex((i) => (Math.min(i, count - 1) + delta + count) % count);
    },
    [count],
  );

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') step(-1);
      if (e.key === 'ArrowRight') step(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, step]);

  if (!shown) return null;

  const templateListing = listing ? bannerListingFrom(listing) : null;

  // Portalled to <body>. This lives inside the preview Card, and a
  // `position: fixed` element resolves against the nearest ancestor with
  // a transform rather than the viewport — so any animated or
  // transformed wrapper up the tree drags the overlay off-centre and
  // clips it. Mounting at the root is the only reliable containment.
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Full creative preview"
    >
      <div
        onClick={onClose}
        className={cn(
          'absolute inset-0 bg-black/70 transition-opacity duration-200 ease-out',
          visible ? 'opacity-100' : 'opacity-0',
        )}
      />
      <div
        className={cn(
          'relative flex max-h-full w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-[hsl(var(--hairline))] bg-card shadow-pop transition-all duration-200 ease-out',
          visible
            ? 'translate-y-0 scale-100 opacity-100'
            : 'translate-y-2 scale-95 opacity-0',
        )}
      >
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-[hsl(var(--hairline))] px-5 py-4">
          <div className="min-w-0">
            <h2 className="truncate font-display text-[18px] font-medium leading-none tracking-[-0.01em]">
              {listing?.title || listing?.area || 'Creative preview'}
            </h2>
            <p className="mt-1.5 truncate font-mono text-[10px] uppercase tracking-[0.06em] text-muted-foreground">
              {count === 0
                ? 'No listings match these filters'
                : `${safeIndex + 1} of ${count}`}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => step(-1)}
              disabled={count < 2}
              aria-label="Previous listing"
            >
              <ChevronLeft />
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => step(1)}
              disabled={count < 2}
              aria-label="Next listing"
            >
              <ChevronRight />
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={onClose}
              aria-label="Close preview"
            >
              <X />
            </Button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-6">
          {!templateListing ? (
            <p className="py-16 text-center font-mono text-[11px] text-muted-foreground">
              Nothing to preview.
            </p>
          ) : (
            // Both canvases on one baseline. The square is given the same
            // width as the vertical so the marks line up across them —
            // that comparison is the whole point of this view.
            <div className="flex flex-wrap items-start justify-center gap-8">
              <Pane label="1:1" caption={BANNER_RATIO_LABEL['1:1']}>
                <ListingBanner
                  width={320}
                  ratio="1:1"
                  brand={{ ...DEFAULT_BRAND, ...brand, radius: 20, qrPosition }}
                  listing={templateListing}
                />
              </Pane>
              {includeVertical && (
                <Pane label="9:16" caption={BANNER_RATIO_LABEL['9:16']}>
                  <ListingBanner
                    width={320}
                    ratio="9:16"
                    brand={{
                      ...DEFAULT_BRAND,
                      ...brand,
                      radius: 20,
                      qrPosition: qrPositionVertical,
                    }}
                    listing={templateListing}
                  />
                </Pane>
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function Pane({
  label,
  caption,
  children,
}: {
  label: string;
  caption: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2">
      {children}
      <div className="text-center">
        <div className="font-mono text-[11px] tabular-nums text-foreground">
          {label}
        </div>
        <div className="font-mono text-[10px] uppercase tracking-[0.06em] text-muted-foreground">
          {caption}
        </div>
      </div>
    </div>
  );
}
